'use client';

import "@/styles/libs/arrow-animation.css";
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { FC, useState } from 'react';
import AnimatedHeight from "./AnimatedHeight";
import MobileDrawerWithTextAndImage from "./MobileDrawerWithTextAndImage";

export type AiFeaturesCondenseProps = {
  backgroundColor: string;
  blurImage: string;
  imageFirst: boolean;
  sectionColorText: string;
  featuresTitleTextColor: string;
  featuresTextColor: string;
  aiFeature: string;
  featuresTitle: React.ReactNode;
  activeFeatureBoxColor: string;
  features: {
    image: React.ReactNode,
    title: React.ReactNode,
    description: string;
  }[]
}

const AiFeaturesCondense: FC<AiFeaturesCondenseProps> = ({
  backgroundColor,
  blurImage,
  imageFirst,
  sectionColorText,
  featuresTitleTextColor,
  featuresTextColor,
  aiFeature,
  featuresTitle,
  activeFeatureBoxColor,
  features = [],
}) => {
  const [activeFeatureIndex, setFeatureIndex] = useState(0);

  return (
    <div className="bg-violet-800 text-white">
      <div className="flex bg-center">
        <div className="mx-auto max-w-[1384px] overflow-hidden">
          <div style={{ backgroundImage: `url(${blurImage})` }} className={`grid gap-2 md:gap-3 text-left ${backgroundColor} ${sectionColorText} bg-center bg-top bg-no-repeat`}>
            <div className="flex justify-left md:justify-center flex-wrap pb-[48px] md:pb-[120px] px-[16px] md:px-0 overflow-hidden">
              <div className={`flex w-full flex-wrap md:flex-nowrap justify-center items-start mt-0 md:mt-[112px] ${imageFirst ? "flex-row-reverse" : null}`}>
                <div className={`flex flex-wrap w-full overflow-hidden md:w-[40%] ${imageFirst ? "mr-0 ml-0 px-0 md:mr-[32px] md:ml-[50px]" : "mr-0 ml-0  md:ml-[32px] md:mr-[140px]"}`}>
                  <div className="flex w-full mb-[24px] mt-[50px] md:mt-0">
                    <div className="flex bg-violet-1000 rounded-[20px] md:ml-[32px] items-center">
                      <div className="flex" id="icon">
                        <span className="flex w-[32px] h-[32px] rounded-[40%] justify-center items-center mr-[8px] mt-[2px] mb-[2px] ml-[2px]">
                          <Image src="https://front.com/webassets/rebrand/ai-icon.svg" width={37} height={37} alt="ai icon" />
                        </span>
                      </div>
                      <div className="flex text-p2 text-white font-500 mr-[13px]" id="textHeader">
                        {aiFeature}
                      </div>
                    </div>
                  </div>
                  <div className={`${featuresTitleTextColor} text-h1 font-500 mb-[24px] md:ml-[32px] w-full`}>{featuresTitle}</div>
                  <div className="flex md:ml-[32px] mb-[24px]">
                    <div className="flex items-center hover-arrow-wrapper">
                      <div className="flex font-500 text-white text-p2 w-full">
                        <Link
                          className="font-500 text-white text-p2"
                          href="/product/ai"
                        > Learn more</Link>
                      </div>
                      <div className="flex ml-[8px] relative w-[16px] h-[16px] bg-[url(https://front.com/webassets/rebrand/arrow-right.png)] hover-arrow" />
                    </div>
                  </div>
                  {/* Mobile nav */}
                  <div className="flex md:hidden flex-wrap">
                    {features.map((feature, index) => (
                      <MobileDrawerWithTextAndImage
                        key={`ai-feature-${index}`}
                        title={feature.title}
                        description={feature.description}
                        image={feature.image}
                        isActive={index === activeFeatureIndex}
                        onClickCallback={() => setFeatureIndex(index)}
                        titleTextColor={featuresTitleTextColor}
                        textColor={featuresTextColor}
                        iconColor="#A857F1"
                        borderColor="border-violet-600"
                      />
                    ))}
                  </div>
                  {/* Desktop nav */}
                  <div className="hidden md:flex flex-wrap">
                    {features.map((feature, index) => (
                      <div key={`ai-feature-${index}`} className={`flex w-full flex-wrap cursor-pointer mt-[24px] rounded-[20px] transition-colors duration-400 ease-in-out-cubic ${activeFeatureIndex === index ? `${activeFeatureBoxColor}` : ''}`}
                        onClick={() => setFeatureIndex(index)}>
                        <motion.div className={`flex ml-[16px] md:ml-[32px] font-500 text-p1 ${featuresTitleTextColor}`}
                          initial={false}
                          animate={{ paddingTop: activeFeatureIndex == index ? "24px" : "8px" }}
                          transition={{
                            duration: 0.4,
                            ease: 'easeInOut',
                          }}
                        >
                          {feature.title}
                        </motion.div>
                        <AnimatedHeight
                          isExpanded={index === activeFeatureIndex}
                        >
                          <div className={`${featuresTextColor}  text-p2 pl-[32px] pr-[32px] pb-[32px] mt-[12px]`}>
                            {feature.description}
                          </div>
                        </AnimatedHeight>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={`hidden md:flex w-[50%] justify-left ${imageFirst ? " ml-[64px]" : "mr-[64px]"}`}>
                  <div className={`relative aspect-square w-full max-w-[612px]`}>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={`ai-feature-${activeFeatureIndex}`}
                        className="absolute inset-0"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                      >
                        {features[activeFeatureIndex].image}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiFeaturesCondense;
